import { inject, ref } from 'vue';
import { defineStore } from 'pinia';
import baseAxios from 'axios';

import { AXIOS } from '../const';

import * as api from '../router/endpoints';

export const useAutonodeStore = defineStore('waitingAutonode', () => {
  const axios = inject(AXIOS) || baseAxios;

  const autonode = ref({});
  const variableList = ref([]);
  const variableTypeList = ref({
    double: 'double',
    boolean: 'boolean',
    array: 'array',
    identificator: 'identificator',
    date: 'date',
    line: 'line',
    point: 'point',
    polygon: 'polygon',
    text: 'text'
  });
  const outputVariableList = ref([]);
  const operationList = ref([]);
  const selectedCustomVariableIndex = ref(null);
  const selectedInstructionStep = ref(null);
  const instruction_steps = ref([]);
  const custom_variables = ref([]);
  const output_entities = ref([]);
  const tableToRegistries = ref([]);
  const direction_variable = ref(null);
  const direction_values = ref({});
  const selectedDirectionIndex = ref(null);
  const selectedOutputIndex = ref(null);
  const selectedTableToRegistryIndex = ref(null);
  const newUuid = ref(null);
  const viewFieldId = ref(null);
  const autonodeRegistryList = ref([]);
  const return_variable = ref(null);
  const return_values = ref({});
  const selectedReturnIndex = ref(null);
  const return_message = ref(null);

  const getVariableListRequest = ref({
    isLoading: false,
    isFinished: false,
    error: null
  });

  const getOutputVariableListRequest = ref({
    isLoading: false,
    isFinished: false,
    error: null
  });

  const getOperationListRequest = ref({
    isLoading: false,
    isFinished: false,
    error: null
  });

  const updateAutonodeRequest = ref({
    isLoading: false,
    isFinished: false,
    error: null
  });

  const generateUuidRequest = ref({
    isLoading: false,
    isFinished: false,
    error: null
  });

  const getAutonodeRequest = ref({
    isLoading: false,
    isFinished: false,
    error: null
  });

  const getAutonodeRegistriesRequest = ref({
    isLoading: false,
    isFinished: false,
    error: null
  });

  function getVariableList(projectId, bpId, nodeId) {
    getVariableListRequest.value = {};
    const req = getVariableListRequest.value;
    req.isLoading = true;
    return axios
      .get(api.waitingAutonodeVariables({ projectId, bpId, nodeId }))
      .then((resp) => {
        variableList.value = resp.data;
      })
      .catch((e) => {
        req.error = e;
        throw e;
      })
      .finally(() => {
        req.isFinished = true;
        req.isLoading = false;
      });
  }

  function getOutputVariableList(projectId, bpId, nodeId) {
    getOutputVariableListRequest.value = {};
    const req = getOutputVariableListRequest.value;
    req.isLoading = true;

    return axios
      .get(api.autonodeOutputVariables({ projectId, bpId, nodeId }))
      .then((resp) => {
        outputVariableList.value = resp.data;
      })
      .catch((e) => {
        req.error = e;
        throw e;
      })
      .finally(() => {
        req.isFinished = true;
        req.isLoading = false;
      });
  }

  function getOperationList(projectId, bpId, nodeId) {
    getOperationListRequest.value = {};
    const req = getOperationListRequest.value;
    req.isLoading = true;
    return axios
      .get(api.waitingAutonodeOperations({ projectId, bpId, nodeId }))
      .then((resp) => {
        operationList.value = resp.data;
      })
      .catch((e) => {
        req.error = e;
        throw e;
      })
      .finally(() => {
        req.isFinished = true;
        req.isLoading = false;
      });
  }

  function updateAutonode(
    projectId,
    bpId,
    nodeId,
    instruction_steps,
    custom_variables,
    output_entities
  ) {
    updateAutonodeRequest.value = {};
    const req = updateAutonodeRequest.value;
    req.isLoading = true;
    let token_view_field_id = viewFieldId.value;
    return axios
      .post(api.waitingAutonode({ projectId, bpId, nodeId }), {
        instruction_steps,
        custom_variables,
        output_entities,
        token_view_field_id,
        registries: tableToRegistries.value,
        directions: {
          direction_variable: direction_variable.value,
          direction_values: direction_values.value
        },
        returns: {
          return_variable: return_variable.value,
          return_values: return_values.value,
          return_message: return_message.value
        }
      })
      .then(() => {})
      .catch((e) => {
        req.error = e;
        throw e;
      })
      .finally(() => {
        req.isFinished = true;
        req.isLoading = false;
      });
  }

  function getAutonode(projectId, bpId, nodeId) {
    getAutonodeRequest.value = {};
    const req = getAutonodeRequest.value;
    req.isLoading = true;
    return axios
      .get(api.waitingAutonode({ projectId, bpId, nodeId }))
      .then((resp) => {
        autonode.value = resp.data;
        viewFieldId.value = resp.data?.token_view_field_id;
        direction_variable.value = resp.data?.directions?.direction_variable;
        direction_values.value = resp.data?.directions?.direction_values ?? {};
        tableToRegistries.value = resp.data?.registries ?? [];
        return_variable.value = resp.data?.returns?.return_variable;
        return_message.value = resp.data?.returns?.return_message;
        return_values.value = resp.data?.returns?.return_values ?? {};
      })
      .catch((e) => {
        req.error = e;
        throw e;
      })
      .finally(() => {
        req.isFinished = true;
        req.isLoading = false;
      });
  }

  function generateUuid() {
    generateUuidRequest.value = {};
    const req = generateUuidRequest.value;
    req.isLoading = true;
    return axios
      .get(api.uuid())
      .then((resp) => {
        newUuid.value = resp.data;
        return resp.data;
      })
      .catch((e) => {
        req.error = e;
        throw e;
      })
      .finally(() => {
        req.isFinished = true;
        req.isLoading = false;
      });
  }

  function getAutonodeRegistryList(projectId, bpId, nodeId) {
    getAutonodeRegistriesRequest.value = {};
    const req = getAutonodeRegistriesRequest.value;
    req.isLoading = true;

    return axios
      .get(api.autonodeRegistries({ projectId, bpId, nodeId }))
      .then((resp) => {
        autonodeRegistryList.value = resp.data;
        return resp.data;
      })
      .catch((e) => {
        req.error = e;
        throw e;
      })
      .finally(() => {
        req.isFinished = true;
        req.isLoading = false;
      });
  }

  function $reset() {
    autonode.value = {};
    variableList.value = [];
    outputVariableList.value = [];
    operationList.value = [];
    selectedCustomVariableIndex.value = null;
    selectedInstructionStep.value = null;
    instruction_steps.value = [];
    custom_variables.value = [];
    output_entities.value = [];
    tableToRegistries.value = [];
    direction_variable.value = null;
    direction_values.value = {};
    selectedDirectionIndex.value = null;
    selectedOutputIndex.value = null;
    selectedTableToRegistryIndex.value = null;
    newUuid.value = null;
    viewFieldId.value = null;
    autonodeRegistryList.value = [];
    return_variable.value = null;
    return_values.value = {};
    selectedReturnIndex.value = null;
    return_message.value = null;

    getVariableListRequest.value = {
      isLoading: false,
      isFinished: false,
      error: null
    };
    getOutputVariableListRequest.value = {
      isLoading: false,
      isFinished: false,
      error: null
    };
    getOperationListRequest.value = {
      isLoading: false,
      isFinished: false,
      error: null
    };
    updateAutonodeRequest.value = {
      isLoading: false,
      isFinished: false,
      error: null
    };
    generateUuidRequest.value = {
      isLoading: false,
      isFinished: false,
      error: null
    };
    getAutonodeRequest.value = {
      isLoading: false,
      isFinished: false,
      error: null
    };
    getAutonodeRegistriesRequest.value = {
      isLoading: false,
      isFinished: false,
      error: null
    };
  }

  return {
    getOperationList,
    getVariableList,
    getOutputVariableList,
    getAutonodeRegistryList,
    updateAutonode,
    generateUuid,
    getAutonode,
    operationList,
    variableList,
    variableTypeList,
    outputVariableList,
    autonodeRegistryList,
    selectedCustomVariableIndex,
    autonode,
    instruction_steps,
    custom_variables,
    output_entities,
    tableToRegistries,
    direction_variable,
    direction_values,
    selectedDirectionIndex,
    selectedInstructionStep,
    selectedOutputIndex,
    selectedTableToRegistryIndex,
    newUuid,
    viewFieldId,
    getOperationListRequest,
    getVariableListRequest,
    getOutputVariableListRequest,
    generateUuidRequest,
    updateAutonodeRequest,
    getAutonodeRequest,
    getAutonodeRegistriesRequest,
    return_variable,
    return_values,
    selectedReturnIndex,
    return_message,

    $reset
  };
});
